import { SignalStatus } from "@/types/api";

export type ResultKey = "PENDING" | "WIN" | "LOSE" | "LIVE" | "UNKNOWN";

export interface ResultPresentation {
  key: ResultKey;
  tr: string;
  en: string;
  text: string;
  bg: string;
  border: string;
  dot: string;
}

const MAP: Record<ResultKey, ResultPresentation> = {
  LIVE: { key: "LIVE", tr: "Canlı", en: "LIVE", text: "text-live-400", bg: "bg-live/10", border: "border-live/30", dot: "bg-live" },
  PENDING: { key: "PENDING", tr: "Bekliyor", en: "Pending", text: "text-gold-400", bg: "bg-gold/10", border: "border-gold/25", dot: "bg-gold" },
  WIN: { key: "WIN", tr: "Kazandı", en: "Win", text: "text-atlas-400", bg: "bg-atlas/12", border: "border-atlas/30", dot: "bg-atlas" },
  LOSE: { key: "LOSE", tr: "Kaybetti", en: "Lose", text: "text-live-400", bg: "bg-live/10", border: "border-live/25", dot: "bg-live" },
  UNKNOWN: { key: "UNKNOWN", tr: "—", en: "—", text: "text-muted-foreground", bg: "bg-white/5", border: "border-white/10", dot: "bg-white/40" },
};

// CANONICAL PRESENTATION LIFECYCLE RESOLVER.
// Reconciles REAL backend state only (no prediction logic).
// Backend enum: status = PENDING | RESOLVED ; won = 1/true (WIN) | 0/false (LOSE) | null.
export function lifecycleOf(
  status: SignalStatus | string | null | undefined,
  won: number | boolean | null | undefined,
  extra?: { resolved_at?: string | null; final_home_goals?: number | null; final_away_goals?: number | null },
): "PENDING" | "WIN" | "LOSE" {
  const wonTrue = won === true || won === 1;
  const wonFalse = won === false || won === 0;
  const s = (status || "").toString().toUpperCase();
  const resolved =
    s === "RESOLVED" || s === "WIN" || s === "LOSE" || wonTrue || wonFalse ||
    !!extra?.resolved_at ||
    (extra?.final_home_goals != null && extra?.final_away_goals != null);
  if (!resolved) return "PENDING";
  if (wonTrue || s === "WIN") return "WIN";
  return "LOSE";
}

export function resolveResult(key: "PENDING" | "WIN" | "LOSE", isLive = false): ResultPresentation {
  if (key === "WIN") return MAP.WIN;
  if (key === "LOSE") return MAP.LOSE;
  return isLive ? MAP.LIVE : MAP.PENDING;
}

export const RESULT_PRESENTATION = MAP;

// Premium (restrained) card surface treatment per lifecycle for instant recognition.
export function resultSurface(key: "PENDING" | "WIN" | "LOSE") {
  if (key === "WIN") return { card: "border-atlas/30 bg-atlas/[0.055]", accent: "bg-atlas", icon: "CheckCircle2", text: "text-atlas-400" };
  if (key === "LOSE") return { card: "border-live/25 bg-live/[0.05]", accent: "bg-live", icon: "XCircle", text: "text-live-400" };
  return { card: "border-gold/20 bg-gold/[0.035]", accent: "bg-gold", icon: "Clock", text: "text-gold-400" };
}

// Parse backend LIVE decision_reason string into structured signals for display.
// Example: "ATLAS LIVE | dominant=Venezia | pressure_gap=14.16 | shots=18 | shots_on=6 | corners=6"
export interface DecisionSignals {
  dominant?: string;
  pressureGap?: number;
  shots?: number;
  shotsOn?: number;
  corners?: number;
  headline?: string;
}

export function parseDecisionReason(reason: string | null | undefined): DecisionSignals {
  if (!reason) return {};
  const out: DecisionSignals = {};
  const parts = reason.split("|").map((p) => p.trim());
  if (parts.length) out.headline = parts[0];
  for (const p of parts) {
    const [kRaw, vRaw] = p.split("=").map((x) => x?.trim());
    if (!vRaw) continue;
    const k = kRaw.toLowerCase();
    const num = Number(vRaw);
    if (k === "dominant") out.dominant = vRaw;
    else if (k === "pressure_gap") out.pressureGap = isNaN(num) ? undefined : num;
    else if (k === "shots") out.shots = isNaN(num) ? undefined : num;
    else if (k === "shots_on") out.shotsOn = isNaN(num) ? undefined : num;
    else if (k === "corners") out.corners = isNaN(num) ? undefined : num;
  }
  return out;
}
